import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "answer", "askButton", "enhanceButton", "loading", "loadingText", "enhancedBadge"]
  static values = {
    askUrl: String,
    enhanceUrl: String
  }

  async ask() {
    const question = this.inputTarget.value.trim()
    if (!question) return

    this.setLoading(true, "Thinking...")

    try {
      const response = await this.postJSON(this.askUrlValue, { question })

      if (response.error) {
        this.renderError(response.error)
      } else {
        this.renderAnswer(response.answer, response.sources)
      }
    } catch (error) {
      this.renderError("Failed to get a response. Please try again.")
    } finally {
      this.setLoading(false)
    }
  }

  async enhance() {
    const question = this.inputTarget.value.trim()
    if (!question) return

    this.setLoading(true, "Enhancing...")

    try {
      const response = await this.postJSON(this.enhanceUrlValue, { question })

      if (response.error) {
        this.renderError(response.error)
      } else {
        this.inputTarget.value = response.enhanced_question
        this.enhancedBadgeTarget.classList.remove("hidden")
      }
    } catch (error) {
      this.renderError("Failed to enhance the question. Please try again.")
    } finally {
      this.setLoading(false)
    }
  }

  handleKeydown(event) {
    if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
      event.preventDefault()
      this.ask()
    }
  }

  // --- Private ---

  async postJSON(url, body) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": csrfToken
      },
      body: JSON.stringify(body)
    })

    return response.json()
  }

  setLoading(active, text = "Thinking...") {
    if (active) {
      this.loadingTarget.classList.remove("hidden")
      this.loadingTextTarget.textContent = text
      this.askButtonTarget.disabled = true
      this.enhanceButtonTarget.disabled = true
    } else {
      this.loadingTarget.classList.add("hidden")
      this.askButtonTarget.disabled = false
      this.enhanceButtonTarget.disabled = false
    }
  }

  renderAnswer(markdown, sources) {
    const html = this.markdownToHTML(markdown)
    let content = `<div class="prose prose-sm max-w-none text-primary">${html}</div>`

    if (sources && sources.length > 0) {
      const sourceItems = sources.map(s => {
        const name = s.name || s.source || s.title || "Source"
        return `<li class="text-xs text-muted">${this.escapeHTML(name)}</li>`
      }).join("")
      content += `<div class="mt-4 pt-3 border-t" style="border-color: var(--border-subtle);"><p class="text-xs font-medium text-muted mb-1">Sources</p><ul class="list-disc list-inside">${sourceItems}</ul></div>`
    }

    this.answerTarget.innerHTML = content
  }

  renderError(message) {
    this.answerTarget.innerHTML = `
      <div class="flex items-center gap-2 text-sm" style="color: var(--red-500);">
        <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"/>
        </svg>
        <span>${this.escapeHTML(message)}</span>
      </div>
    `
  }

  markdownToHTML(text) {
    if (!text) return ""

    let html = this.escapeHTML(text)

    // Code blocks (``` ... ```)
    html = html.replace(/```(\w*)\n([\s\S]*?)```/g, '<pre class="bg-surface-1 rounded p-3 overflow-x-auto text-xs my-2"><code>$2</code></pre>')

    // Inline code
    html = html.replace(/`([^`]+)`/g, '<code class="px-1 py-0.5 rounded text-xs" style="background-color: var(--surface-1);">$1</code>')

    // Headers
    html = html.replace(/^### (.+)$/gm, '<h3 class="heading-3 mt-3 mb-1">$1</h3>')
    html = html.replace(/^## (.+)$/gm, '<h2 class="heading-2 mt-4 mb-2">$1</h2>')
    html = html.replace(/^# (.+)$/gm, '<h1 class="heading-1 mt-4 mb-2">$1</h1>')

    // Bold and italic
    html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    html = html.replace(/\*(.+?)\*/g, "<em>$1</em>")

    // Unordered lists
    html = html.replace(/^- (.+)$/gm, '<li class="ml-4 list-disc">$1</li>')
    html = html.replace(/(<li.*<\/li>\n?)+/g, '<ul class="my-2">$&</ul>')

    // Ordered lists
    html = html.replace(/^\d+\. (.+)$/gm, '<li class="ml-4 list-decimal">$1</li>')

    // Line breaks (double newline = paragraph)
    html = html.replace(/\n\n/g, "</p><p class=\"my-2\">")
    html = `<p class="my-2">${html}</p>`

    return html
  }

  escapeHTML(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
